import React, { useState, useMemo, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { isEmpty, reduce, compact } from 'lodash';
import { FiPlus } from 'react-icons/fi';
import { useDispatch, useSelector } from 'react-redux';
import { Modal, Checkbox, Loader, Button, TableSizeControls } from 'ui-kit';
import ListTable from 'modules/ListTable';
import * as Cells from 'modules/ListTable/cells';
import * as Scaffold from 'modules/ListScaffold';
import Filter from 'ui-kit/old/filter';
import IsMdAccount from 'modules/AccessControl/isMdAccount';
import { fetchRetailers } from '_entities/old/Retailers/retailers.service';
import { isValidDate } from 'utils/old/checkSearchDate';
import apiData from '_entities/old/api/config';
import axios from 'axios';
import { API_URLS, BUTTON_TYPES } from '_constants';
import { notification } from 'utils/services';
import * as Styled from '../styles';
import * as Styles from './styles';

const { baseUrl } = apiData;

// Kept local rather than imported from CampaignView's sections.config: a modal in modules/ should
// not depend on a page's constants. Same copy the field showed as its native placeholder before.
const RETAILER_SEARCH_PLACEHOLDER = 'Search by retailer name, address, city and/or zip code';

const addExistingRetailersToTheCampaignRequest = async (data, campaignId) => {
  const token = localStorage.getItem('Authorization');
  return axios.post(`${baseUrl}${API_URLS.campaigns}/${campaignId}/retailers/add`, data, {
    headers: {
      Authorization: `Token ${token}`,
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
  });
};

function AddExistingRetailerModal({ isOpen, onClose, campaignId, loadAssignments }) {
  const [currentPerPage, setCurrentPerPage] = useState(20);
  const [selectedRetailersIds, setSelectedRetailersIds] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [searchValue, setSearchValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sortOption, setSortOption] = useState({ sortBy: 'name', sortOrder: 1 });
  const isMdAccount = IsMdAccount();
  const dispatch = useDispatch();

  const {
    oldRetailers: {
      retailersTableData: { pagination, data },
    },
  } = useSelector((state) => state);

  const hasSelection = Object.values(selectedRetailersIds).some(Boolean);

  const handleProceedSubmition = async () => {
    const retailersToAdd = reduce(
      selectedRetailersIds,
      (acc, value, key) => (value ? [...acc, { type: 'retailers', id: key }] : acc),
      [],
    );
    if (isEmpty(retailersToAdd)) {
      notification.error('Select retailer(s) you want to add to the campaign');
      return;
    }
    try {
      setIsLoading(true);
      await addExistingRetailersToTheCampaignRequest({ data: retailersToAdd }, campaignId);
      await loadAssignments({ page: 1 });
      notification.success('Retailer(s) were successfully added to the campaign');
    } catch (err) {
      const errorMessageArray = err?.response?.data?.errors;
      notification.error(
        Array.isArray(errorMessageArray)
          ? errorMessageArray[0]?.detail || 'Something went wrong during the request'
          : 'Something went wrong during the request',
      );
    } finally {
      setIsLoading(false);
      onClose();
    }
  };

  const loadRetailers = useCallback(
    async ({
      page = currentPage,
      perPage = currentPerPage,
      sortBy = sortOption.sortBy,
      sortOrder = sortOption.sortOrder,
      ...rest
    } = {}) => {
      try {
        setIsLoading(true);
        await fetchRetailers(
          {
            ...rest,
            page,
            perPage,
            sortBy,
            sortOrder,
            is_current: ['t'],
            include: 'aggregationpolygon_set',
            without_campaign_id: campaignId,
          },
          dispatch,
        );
      } catch (e) {
        // Non-fatal: loading state cleared in finally; table shows empty.
      } finally {
        setIsLoading(false);
      }
    },
    [currentPage, dispatch, currentPerPage, sortOption, campaignId],
  );

  const sortHandler = useCallback(
    (sortBy, sortOrder) => {
      setSortOption({ sortBy, sortOrder });
      loadRetailers({ sortBy, sortOrder });
    },
    [loadRetailers],
  );

  // Initial load fires once on mount; loadRetailers is intentionally excluded so it isn't
  // re-triggered when page/perPage/sort change (those paths call loadRetailers explicitly).
  useEffect(() => {
    loadRetailers({ page: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCheckbox = useCallback(({ target: { name, value } }) => {
    setSelectedRetailersIds((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleOnChangeInputSearch = (val) => {
    if (isEmpty(val)) setSearchValue('');
    setSearchValue(val);
  };

  const submitSearch = () => {
    if (isEmpty(searchValue)) return;
    setCurrentPage(1);
    loadRetailers({ page: 1, search: isValidDate(searchValue).trim() });
  };

  const handleKeyDown = (e) => {
    if (e.keyCode === 13) {
      e.preventDefault();
      submitSearch();
    }
  };

  const handleRemoveSearch = () => {
    if (isEmpty(searchValue)) return;
    loadRetailers({ page: 1, search: '' });
    setSearchValue('');
  };

  const handlePerPageChange = useCallback(
    ({ value }) => {
      if (currentPerPage === value) return;
      setCurrentPerPage(value);
      setCurrentPage(1);
      loadRetailers({ perPage: value, page: 1, search: searchValue });
    },
    [loadRetailers, searchValue, currentPerPage],
  );

  const handlePageChange = useCallback(
    ({ value }) => {
      if (currentPage === value) return;
      setCurrentPage(value);
      loadRetailers({ page: value, search: searchValue });
    },
    [loadRetailers, searchValue, currentPage],
  );

  const columns = useMemo(
    () =>
      compact([
        {
          key: 'select',
          width: '52px',
          center: true,
          render: (row) => (
            <Checkbox name={String(row.id)} value={!!selectedRetailersIds[row.id]} onChange={handleCheckbox} />
          ),
        },
        { key: 'name', label: 'Name', sortKey: 'name', render: (row) => <Cells.Text>{row.name || ''}</Cells.Text> },
        isMdAccount && { key: 'atcc', label: 'ATCC', render: (row) => <Cells.Text>{row.atcc || ''}</Cells.Text> },
        {
          key: 'address',
          label: 'Address',
          sortKey: 'address',
          render: (row) => <Cells.Text>{row.address || ''}</Cells.Text>,
        },
        { key: 'city', label: 'City', sortKey: 'city', render: (row) => <Cells.Text>{row.city || ''}</Cells.Text> },
        {
          key: 'zipcode',
          label: 'Zip code',
          sortKey: 'zipcode',
          render: (row) => <Cells.Text>{row.zipcode || ''}</Cells.Text>,
        },
        {
          key: 'county_name',
          label: 'County',
          sortKey: 'county_name',
          render: (row) => <Cells.Text>{row.county_name || ''}</Cells.Text>,
        },
      ]),
    [selectedRetailersIds, handleCheckbox, isMdAccount],
  );

  const toolbar = {
    left: (
      <Scaffold.LeftCluster>
        <Scaffold.SearchWrap>
          {/* placeholder is empty on purpose: the marquee below renders it instead, so the two
              never double up. See Scaffold.PlaceholderMarquee. */}
          <Filter
            type="button"
            hideFilterBtn
            placeholder=""
            onChangeInput={handleOnChangeInputSearch}
            handleClickInput={submitSearch}
            onKeyDown={handleKeyDown}
            onRemoveSearch={handleRemoveSearch}
            searchValue={searchValue}
            isWidth
          />
          {isEmpty(searchValue) && (
            <Scaffold.PlaceholderMarquee aria-hidden="true">
              <span>{RETAILER_SEARCH_PLACEHOLDER}</span>
            </Scaffold.PlaceholderMarquee>
          )}
        </Scaffold.SearchWrap>
      </Scaffold.LeftCluster>
    ),
    actions: (
      <Button
        variant={BUTTON_TYPES.PRIMARY}
        text="Add"
        icon={<FiPlus size={16} />}
        onClick={handleProceedSubmition}
        disabled={!hasSelection || isLoading}
      />
    ),
  };

  if (!isOpen) return null;

  return (
    // Loader is a fixed overlay above the modal (z-index 9999 vs 9000), so it is rendered as a
    // sibling of the body rather than in place of the modal — otherwise every search, sort, page
    // change and the Add submit would unmount the table and remount it.
    <Modal
      open={isOpen}
      onClose={isLoading ? () => {} : onClose}
      preventEscape
      preventDesktopOverflow
      className="add_retailers_modal"
    >
      {isLoading && <Loader />}
      <Styles.ModalWrapper>
        <Styled.ModalTitle>Add existing retailer</Styled.ModalTitle>
        <Styled.ModalDescription>Select the retailer you'd like to add to this campaign</Styled.ModalDescription>
        <ListTable
          columns={columns}
          data={data || []}
          rowKey={(row) => String(row.id)}
          sortOption={sortOption}
          sortHandler={sortHandler}
          emptyText="No retailers found."
          toolbar={toolbar}
          footer={
            <TableSizeControls
              itemsPerPageProps={{ handlePerPageChange, value: currentPerPage }}
              pageCountProps={{ value: currentPage, pages: pagination?.pages || 1, handlePageChange }}
              paginationInfoProps={{ total: pagination?.count || 0, currentPage, perPage: currentPerPage }}
              paginationNavProps={{
                currentPage,
                handlePageChange,
                pages: pagination?.pages || 1,
                titleTable: 'Retailers',
              }}
            />
          }
        />
      </Styles.ModalWrapper>
    </Modal>
  );
}

AddExistingRetailerModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  loadAssignments: PropTypes.func,
  campaignId: PropTypes.string,
};

AddExistingRetailerModal.defaultProps = {
  campaignId: '',
  loadAssignments: () => {},
};

export default AddExistingRetailerModal;
